// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    Field,
    Input,
    makeStyles,
    RadioGroup,
    Textarea,
} from '@fluentui/react-components';
import { LockClosed32Regular, LockOpen32Regular } from '@fluentui/react-icons';
import { CoursePlatform, Curriculum } from '@microsoft/vscodeedu-api';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Html5ColorIcon, PythonColorIcon } from '../../icons/code';
import { MaxDescriptionLength, MaxTitleLength } from '../../models';
import { useAppStyles } from '../../styles';
import { getCoursePlatformText, IntlProps, RadioCard, TrackContext } from '../common';
import { CancelButton, ConfirmButton, DialogHeader } from '../dialogs';

const useStyles = makeStyles({
    templateList: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '0.5rem',
        marginBottom: '1rem',
    },
});

// Create activity dialog props.
export type CreateActivityDialogProps = IntlProps<
    Partial<DialogProps> & {
        onCreate: (props: Pick<Curriculum, 'title' | 'description' | 'platform' | 'repoIsPublic'>) => void;
    }
>;

// Create activity dialog.
export const CreateActivityDialog = injectIntl((props: CreateActivityDialogProps) => {
    const { intl, onCreate, open } = props;
    const appStyles = useAppStyles();
    const styles = useStyles();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [platform, setPlatform] = useState<CoursePlatform | undefined>();
    const [repoType, setRepoType] = useState<'private' | 'public'>('private');

    // Reset state on reopening the dialog.
    useEffect(() => {
        if (open === true) {
            setTitle('');
            setDescription('');
            setPlatform(undefined);
            setRepoType('private');
        }
    }, [open]);

    const onConfirm = useCallback(() => {
        const trimmedDescription = description.trim();
        onCreate({
            platform: platform!,
            title: title.trim(),
            description: trimmedDescription.length > 0 ? trimmedDescription : undefined,
            repoIsPublic: repoType === 'public',
        });
    }, [description, onCreate, platform, repoType, title]);

    return (
        <TrackContext.Provider value={{ container: 'create-activity-dialog' }}>
            <Dialog {...props}>
                <DialogSurface className={appStyles.dialogNarrow}>
                    <DialogBody>
                        <DialogHeader
                            title={intl.formatMessage({
                                defaultMessage: 'Create an activity',
                                description: 'Create activity dialog title.',
                            })}
                        />
                        <DialogContent>
                            <Field
                                className={appStyles.dialogField}
                                required
                                label={intl.formatMessage({
                                    defaultMessage: 'Title',
                                    description: 'Label for the activity title text box.',
                                })}
                            >
                                <Input
                                    value={title}
                                    onChange={(_, { value }) => setTitle(value)}
                                    maxLength={MaxTitleLength}
                                    placeholder={intl.formatMessage({
                                        defaultMessage: 'Name your activity',
                                        description: 'Placeholder text inside the activity title text box.',
                                    })}
                                />
                            </Field>
                            <Field
                                className={appStyles.dialogField}
                                label={intl.formatMessage({
                                    defaultMessage: 'Description (optional)',
                                    description: 'Label for the activity description text box.',
                                })}
                            >
                                <Textarea
                                    value={description}
                                    onChange={(_, { value }) => setDescription(value)}
                                    maxLength={MaxDescriptionLength}
                                    placeholder={intl.formatMessage({
                                        defaultMessage: 'Tell us about your activity...',
                                        description: 'Placeholder text inside the activity description text box.',
                                    })}
                                />
                            </Field>
                            <Field
                                required
                                label={intl.formatMessage({
                                    defaultMessage: 'Select a template',
                                    description: 'Label for template selection radio group.',
                                })}
                            >
                                <RadioGroup className={styles.templateList}>
                                    <RadioCard
                                        key="python"
                                        icon={<PythonColorIcon />}
                                        title={getCoursePlatformText('python')}
                                        description={
                                            <FormattedMessage
                                                defaultMessage="Python programming course."
                                                description="Course type description."
                                            />
                                        }
                                        selected={platform === 'python'}
                                        onChange={() => setPlatform('python')}
                                    />
                                    <RadioCard
                                        key="html"
                                        icon={<Html5ColorIcon />}
                                        title={getCoursePlatformText('html')}
                                        description={
                                            <FormattedMessage
                                                defaultMessage="Web development course."
                                                description="Course type description."
                                            />
                                        }
                                        selected={platform === 'html'}
                                        onChange={() => setPlatform('html')}
                                    />
                                </RadioGroup>
                            </Field>
                            <Field
                                className={appStyles.dialogField}
                                required
                                label={intl.formatMessage({
                                    defaultMessage: 'GitHub repository permissions',
                                    description: 'Label for the permissions field.',
                                })}
                            >
                                <RadioGroup className={styles.templateList}>
                                    <RadioCard
                                        key="private"
                                        icon={<LockClosed32Regular />}
                                        title={intl.formatMessage({
                                            defaultMessage: 'Private',
                                            description: 'Permission level title.',
                                        })}
                                        description={
                                            <FormattedMessage
                                                defaultMessage="Only accessible to you."
                                                description="Permission level description."
                                            />
                                        }
                                        selected={repoType === 'private'}
                                        onChange={() => setRepoType('private')}
                                    />
                                    <RadioCard
                                        key="public"
                                        icon={<LockOpen32Regular />}
                                        title={intl.formatMessage({
                                            defaultMessage: 'Public',
                                            description: 'Permission level title.',
                                        })}
                                        description={
                                            <FormattedMessage
                                                defaultMessage="Viewable by any Internet user."
                                                description="Permission level description."
                                            />
                                        }
                                        selected={repoType === 'public'}
                                        onChange={() => setRepoType('public')}
                                    />
                                </RadioGroup>
                            </Field>
                        </DialogContent>
                        <DialogActions>
                            <CancelButton />
                            <ConfirmButton
                                appearance="primary"
                                disabled={!(title.trim() && platform !== undefined)}
                                onClick={onConfirm}
                            >
                                <FormattedMessage
                                    description="Create new activity dialog confirm button."
                                    defaultMessage="Create activity"
                                />
                            </ConfirmButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </TrackContext.Provider>
    );
});

export default CreateActivityDialog;
